import { createRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';

const isKeyboardEventType = (e: unknown): e is KeyboardEvent => !!e && typeof e === 'object' && 'key' in e;

const wrapOutOfBoundIndex = (index: number, arrayLength: number) => {
	if (arrayLength === 1) {
		return 0;
	}

	const indexModLength = index % arrayLength;

	return index < 0 ? arrayLength + indexModLength : indexModLength;
};

interface UseKeyboardListControlConfig<T = unknown> {
	emitClickOnSelect?: boolean;
	focusSelected?: boolean;
	onEscapeKeyDown?: VoidFunction;
	onSelectOption?: (option: T) => void;
	options: T[];
	selectOnTab?: boolean;
}

enum Key {
	Down = 'ArrowDown',
	Enter = 'Enter',
	Escape = 'Escape',
	Tab = 'Tab',
	Up = 'ArrowUp',
}

interface Interactable {
	click: VoidFunction;
	focus: VoidFunction;
}

export const useKeyboardListControl = <T = unknown, U = HTMLElement>({
	emitClickOnSelect = false,
	focusSelected = false,
	onEscapeKeyDown,
	onSelectOption,
	options,
	selectOnTab = true,
}: UseKeyboardListControlConfig<T>) => {
	const [selectedOption, setSelectedOption] = useState<T | undefined>(options[0]);

	const refMap = useMemo(() => new Map(options.map((option) => [option, createRef<U>()])), [options]);

	useLayoutEffect(() => {
		setSelectedOption(options[0]);
	}, [options]);

	const isSelected = (option: T) => option === selectedOption;

	const register = (option: T) => ({
		onClick: () => onSelectOption?.(option),
		onMouseOver: () => setSelectedOption(option),
		...((emitClickOnSelect || focusSelected) && { ref: refMap.get(option) }),
		onMouseOut: () => setSelectedOption(undefined),
	});

	useEffect(() => {
		const onPressKey = (e: Event) => {
			if (!isKeyboardEventType(e)) {
				return;
			}

			const key = e.key as Key;

			if (!Object.values(Key).includes(key)) {
				return;
			}

			if (e.cancelable) {
				e.preventDefault();
			}

			if (key === Key.Escape && onEscapeKeyDown) {
				onEscapeKeyDown();
				return;
			}

			if (key === Key.Enter || (key === Key.Tab && selectOnTab)) {
				if (selectedOption === undefined) {
					return;
				}

				onSelectOption?.(selectedOption);

				if (emitClickOnSelect) {
					(refMap.get(selectedOption)?.current as unknown as Interactable)?.click?.();
				}

				return;
			}

			if (!options.length) {
				return;
			}

			const direction = key === Key.Down || key === Key.Tab ? 1 : -1;
			const selectedOptionIndex = selectedOption === undefined ? -1 : options.indexOf(selectedOption);
			const newOption =
				selectedOptionIndex === -1
					? options[direction === 1 ? 0 : options.length - 1]
					: options[wrapOutOfBoundIndex(selectedOptionIndex + direction, options.length)];

			if (focusSelected) {
				(refMap.get(newOption)?.current as unknown as Interactable)?.focus?.();
			}

			setSelectedOption(newOption);
		};

		window.addEventListener('keydown', onPressKey);

		return () => window.removeEventListener('keydown', onPressKey);
	}, [emitClickOnSelect, focusSelected, onEscapeKeyDown, onSelectOption, options, refMap, selectOnTab, selectedOption]);

	return {
		isSelected,
		register,
	};
};
